// VendAgain — public browse page. Filter by section (Business/Personal),
// category, city, and text search. Anonymous users see everything;
// authenticated users get a "Post a listing" CTA in the hero.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Plus, Package, Compass, Hand } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { AiFreeBadge } from "@/components/AiFreeToggle";
import { CATEGORIES, SECTIONS, CAT_MAP, CategoryChip, ConditionPill, formatPrice } from "./shared";

export default function VendAgainBrowse() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [section, setSection] = useState("");
  const [category, setCategory] = useState("");
  const [aiFree, setAiFree] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (city.trim()) params.city = city.trim();
      if (section) params.section = section;
      if (category) params.category = category;
      if (aiFree) params.ai_free = true;
      const { data } = await api.get("/vendagain/listings", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, city, section, category, aiFree]);

  const grouped = useMemo(() => {
    // When no explicit filter, split into Business + Personal sections
    // so the layout feels like the two collections the user described.
    if (section || category) return null;
    const m = { business: [], personal: [] };
    items.forEach((i) => { (m[i.section] || m.personal).push(i); });
    return m;
  }, [items, section, category]);

  const visibleCategories = section
    ? CATEGORIES.filter((c) => c.section === section)
    : CATEGORIES;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14" data-testid="vendagain-browse">
      {/* HERO */}
      <div
        className="relative overflow-hidden rounded-sm p-10 md:p-14 mb-10"
        style={{
          background: "linear-gradient(135deg, var(--avt-teal) 0%, var(--avt-purple) 55%, var(--avt-pink) 100%)",
        }}
      >
        <div className="relative text-white max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1 rounded-full mb-5 font-bold"
               style={{ background: "rgba(255,255,255,0.18)" }}>
            <Package className="h-3 w-3" /> VendAgain
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
            Don&rsquo;t need it? Vend it.
          </h1>
          <p className="mt-4 text-base sm:text-lg leading-relaxed opacity-95">
            Post the fryer you outgrew, the fixture that didn&rsquo;t sell, the
            couch you replaced. Community items go here — first to the makers
            and organizers you already trust.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {user ? (
              <Link to="/vendagain/new" data-testid="vendagain-post-btn">
                <Button className="btn-primary rounded-full inline-flex items-center gap-2">
                  <Plus size={16} /> Post an item
                </Button>
              </Link>
            ) : (
              <Link to="/register" data-testid="vendagain-signup-btn">
                <Button className="btn-primary rounded-full">
                  Sign up to post
                </Button>
              </Link>
            )}
            {user && (
              <Link to="/vendagain/mine" data-testid="vendagain-mine-btn">
                <Button variant="outline" className="rounded-full border-white text-white hover:bg-white/10">
                  My listings
                </Button>
              </Link>
            )}
            <Link to="/vendagain/iso" data-testid="vendagain-iso-cta">
              <Button
                variant="outline"
                className="rounded-full border-white text-white hover:bg-white/10 inline-flex items-center gap-2"
              >
                <Compass size={14} /> In Search Of
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="mb-8 space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
            <Input
              placeholder="Search titles or descriptions…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 rounded-full"
              data-testid="vendagain-search-input"
            />
          </div>
          <div className="relative min-w-[180px]">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
            <Input
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="pl-9 rounded-full"
              data-testid="vendagain-city-input"
            />
          </div>
        </div>
        {/* Section chips */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="vendagain-section-all"
            onClick={() => { setSection(""); setCategory(""); }}
            className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full transition ${!section ? "" : "opacity-60 hover:opacity-100"}`}
            style={{
              background: !section ? "var(--avt-purple)" : "rgba(0,0,0,0.06)",
              color: !section ? "#fff" : "var(--ink-soft)",
            }}
          >
            All
          </button>
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              data-testid={`vendagain-section-${s.key}`}
              onClick={() => { setSection(s.key); setCategory(""); }}
              className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full transition ${section === s.key ? "" : "opacity-60 hover:opacity-100"}`}
              style={{
                background: section === s.key ? "var(--avt-purple)" : "rgba(0,0,0,0.06)",
                color: section === s.key ? "#fff" : "var(--ink-soft)",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        {/* Category chips (context-aware) */}
        <div className="flex flex-wrap gap-2 items-center">
          {visibleCategories.map((c) => {
            const active = category === c.value;
            const Icon = c.icon;
            return (
              <button
                key={c.value}
                type="button"
                data-testid={`vendagain-cat-filter-${c.value}`}
                onClick={() => {
                  const next = active ? "" : c.value;
                  setCategory(next);
                  if (next && !section) setSection(c.section);
                }}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full transition ${active ? "ring-2 ring-offset-1" : ""}`}
                style={{
                  background: c.color,
                  color: c.ink,
                }}
              >
                <Icon size={12} /> {c.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setAiFree((v) => !v)}
            data-testid="vendagain-filter-ai-free"
            aria-pressed={aiFree}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full transition"
            style={{
              background: aiFree ? "var(--moss)" : "rgba(111,125,85,0.14)",
              color: aiFree ? "#fff" : "var(--moss)",
            }}
          >
            <Hand size={12} /> Handmade only
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center opacity-60" data-testid="vendagain-loading">Loading listings…</div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center border-2 border-dashed rounded-sm"
             data-testid="vendagain-empty"
             style={{ borderColor: "rgba(0,0,0,0.08)" }}>
          <Package className="mx-auto h-10 w-10 opacity-30" />
          <h3 className="mt-4 font-serif text-2xl">No listings match yet.</h3>
          <p className="mt-2 text-sm opacity-70">
            Try clearing the filters — or be the first to post something.
          </p>
        </div>
      ) : grouped ? (
        <div className="space-y-14">
          {SECTIONS.map((s) => (
            grouped[s.key].length > 0 && (
              <SectionBlock key={s.key} section={s} items={grouped[s.key]} />
            )
          ))}
        </div>
      ) : (
        <ListingsGrid items={items} />
      )}
    </div>
  );
}

function SectionBlock({ section, items }) {
  return (
    <section data-testid={`vendagain-section-block-${section.key}`}>
      <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
        <div>
          <div className="overline" style={{ color: "var(--avt-purple)" }}>
            {section.label}
          </div>
          <p className="text-sm opacity-70">{section.tagline}</p>
        </div>
        <span className="text-xs opacity-60">{items.length} listing{items.length === 1 ? "" : "s"}</span>
      </div>
      <ListingsGrid items={items} />
    </section>
  );
}

function ListingsGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vendagain-grid">
      {items.map((i) => (
        <Link
          key={i.id}
          to={`/vendagain/listings/${i.id}`}
          className="card-warm rounded-sm overflow-hidden block group"
          data-testid={`vendagain-card-${i.id}`}
        >
          <div className="aspect-[4/3] overflow-hidden bg-[color:var(--sand)] relative">
            {(i.image_urls && i.image_urls[0]) ? (
              <img
                src={i.image_urls[0]}
                alt={i.title}
                className="w-full h-full object-cover group-hover:scale-[1.02] transition"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {(() => {
                  const cat = CAT_MAP[i.category];
                  if (!cat) return null;
                  const Icon = cat.icon;
                  return <Icon className="h-12 w-12 opacity-30" style={{ color: cat.ink }} />;
                })()}
              </div>
            )}
            {i.sold && (
              <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                   style={{ background: "rgba(0,0,0,0.7)", color: "#fff" }}>
                Sold
              </div>
            )}
            <div className="absolute top-2 right-2">
              <span
                className="text-sm font-bold px-3 py-1 rounded-full"
                style={{
                  background: "rgba(255,255,255,0.95)",
                  color: "var(--ink)",
                }}
                data-testid={`vendagain-card-price-${i.id}`}
              >
                {formatPrice(i.price_cents, i.best_offer)}
              </span>
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between gap-2 mb-2">
              <CategoryChip value={i.category} />
              <div className="inline-flex items-center gap-1.5">
                <AiFreeBadge ai_free={i.owner?.ai_free} size="xs" />
                <ConditionPill value={i.condition} />
              </div>
            </div>
            <h3 className="font-serif text-xl leading-tight line-clamp-2">{i.title}</h3>
            <div className="mt-2 text-xs opacity-70 inline-flex items-center gap-1">
              <MapPin size={11} /> {i.city}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
